<template>
  <div ref="wrapperRef" class="app-icon-button-wrapper" @mouseenter="onEnter" @mouseleave="onLeave">
    <button
      type="button"
      :class="resolvedButtonClass"
      :disabled="disabled || loading"
      :aria-label="label || undefined"
      v-bind="$attrs"
      @click="handleClick"
    >
      <span :class="resolvedContentClass">
        <slot />
      </span>
      <span v-if="loading" class="app-icon-button-loader-block" aria-hidden="true" />
    </button>
  </div>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="visible && label && !tooltipDisabled"
        class="app-icon-button-tooltip"
        :style="tooltipStyle"
        role="tooltip"
      >
        {{ label }}
      </div>
    </transition>
  </teleport>
</template>

<script>
  import { nextTick } from 'vue';

  export default {
    name: 'AppIconButton',
    inheritAttrs: false,
    props: {
      label: {
        type: String,
        default: '',
      },
      buttonClass: {
        type: [String, Array, Object],
        default: '',
      },
      iconClass: {
        type: [String, Array, Object],
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      tooltipDisabled: {
        type: Boolean,
        default: false,
      },
      tooltipDelay: {
        type: Number,
        default: 400,
      },
      variant: {
        type: String,
        default: 'soft',
      },
      tone: {
        type: String,
        default: 'neutral',
      },
      size: {
        type: String,
        default: 'sm',
      },
      active: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['click'],
    data() {
      return {
        visible: false,
        hoverTimer: null,
        tooltipStyle: {},
      };
    },
    computed: {
      sizeClass() {
        const size = this.size || 'sm';

        if (size === 'lg') return 'app-icon-button--lg';
        if (size === 'md') return 'app-icon-button--md';
        return 'app-icon-button--sm';
      },
      resolvedButtonClass() {
        return [
          'app-icon-button',
          this.sizeClass,
          { 'is-loading': this.loading },
          this.buttonClass,
        ];
      },
      resolvedContentClass() {
        return ['app-icon-button-content', this.iconClass, { 'is-loading': this.loading }];
      },
    },
    methods: {
      updateTooltipPosition() {
        const wrapper = this.$refs.wrapperRef;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        this.tooltipStyle = {
          left: `${rect.left + rect.width / 2}px`,
          top: `${rect.top - 6}px`,
        };
      },
      handleClick(event) {
        if (this.disabled || this.loading) {
          event.preventDefault();
          return;
        }
        this.$emit('click', event);
      },
      onEnter() {
        if (this.tooltipDisabled || !this.label) {
          return;
        }
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => {
          this.visible = true;
          nextTick(() => {
            this.updateTooltipPosition();
          });
        }, this.tooltipDelay);
      },
      onLeave() {
        clearTimeout(this.hoverTimer);
        this.visible = false;
      },
    },
    mounted() {
      window.addEventListener('scroll', this.updateTooltipPosition, true);
      window.addEventListener('resize', this.updateTooltipPosition);
    },
    beforeUnmount() {
      clearTimeout(this.hoverTimer);
      window.removeEventListener('scroll', this.updateTooltipPosition, true);
      window.removeEventListener('resize', this.updateTooltipPosition);
    },
  };
</script>

<style scoped>
  .app-icon-button-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .app-icon-button {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    outline: none;
    flex-shrink: 0;
    position: relative;
    color: inherit;
    box-shadow: none;
    /* Allow button-class prop to control colors; fallback to CSS vars if no classes applied */
    border-width: 1px;
    border-style: solid;
    border-color: rgb(var(--color-border-default) / 1);
    background: rgb(var(--color-surface-base) / 1);
    color: rgb(var(--color-text-primary) / 1);
  }

  /* Only apply CSS variable defaults if no utility classes are forcing colors */
  .app-icon-button:not([class*='bg-']):not([class*='border-']) {
    border-color: rgb(var(--color-border-default) / 1);
    background: rgb(var(--color-surface-base) / 1);
  }

  .dark .app-icon-button:not([class*='text-']) {
    color: rgb(var(--color-text-primary) / 1);
  }

  .app-icon-button--sm {
    width: 25px;
    height: 25px;
    border-radius: 6px;
  }

  .app-icon-button--md {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }

  .app-icon-button--lg {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }

  .app-icon-button.is-loading {
    border-color: rgb(var(--color-state-focus) / 0.65);
    box-shadow: 0 0 0 1px rgb(var(--color-state-focus) / 0.08);
    animation: app-icon-button-border-pulse 1.05s ease-in-out infinite;
  }

  .dark .app-icon-button.is-loading {
    border-color: rgb(var(--color-state-focus) / 0.58);
    box-shadow: 0 0 0 1px rgb(var(--color-state-focus) / 0.08);
  }

  .app-icon-button:focus,
  .app-icon-button:focus-visible {
    outline: none;
    border-color: rgb(var(--color-state-focus) / 1);
    box-shadow: 0 0 0 2px rgb(var(--color-state-focus) / 0.15);
  }

  .app-icon-button:hover:not(:disabled) {
    border-color: rgb(var(--color-state-focus) / 0.55);
    box-shadow: 0 0 0 2px rgb(var(--color-state-focus) / 0.08);
  }

  .dark .app-icon-button:hover:not(:disabled) {
    border-color: rgb(var(--color-state-focus) / 0.55);
    box-shadow: 0 0 0 2px rgb(var(--color-state-focus) / 0.12);
  }

  .app-icon-button:disabled {
    cursor: wait;
  }

  .app-icon-button-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }

  .app-icon-button-content.is-loading {
    opacity: 0;
    visibility: hidden;
    transform: scale(0.88);
  }

  .app-icon-button-loader-block {
    position: absolute;
    width: 11px;
    height: 11px;
    border-radius: 3px;
    border: 1px solid rgb(var(--color-text-primary) / 0.12);
    background: linear-gradient(
      135deg,
      rgb(var(--color-brand-primary-hover) / 0.34),
      rgb(var(--color-brand-primary) / 0.26)
    );
    opacity: 0.42;
    transform: scale(0.86);
    animation: app-icon-button-loader-pulse 0.95s ease-in-out infinite;
    will-change: transform, opacity, filter;
  }

  .dark .app-icon-button-loader-block {
    border-color: rgb(var(--color-static-white) / 0.14);
    background: linear-gradient(
      135deg,
      rgb(var(--color-brand-primary-hover) / 0.3),
      rgb(var(--color-brand-primary) / 0.26)
    );
  }

  .app-icon-button :deep(svg) {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    display: block;
  }

  .app-icon-button-tooltip {
    position: fixed;
    transform: translate(-50%, calc(-100% - 6px));
    z-index: 200;
    white-space: nowrap;
    background: rgb(var(--color-surface-inverse) / 1);
    color: rgb(var(--color-text-inverse) / 1);
    font-size: 0.6875rem;
    font-weight: 500;
    line-height: 1.4;
    padding: 3px 8px;
    border-radius: 4px;
    pointer-events: none;
    box-shadow: 0 2px 8px rgb(var(--color-static-black) / 0.25);
  }

  .app-icon-button-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgb(var(--color-surface-inverse) / 1);
  }

  @keyframes app-icon-button-loader-pulse {
    0% {
      opacity: 0.34;
      transform: scale(0.82);
      filter: saturate(0.92);
    }
    45% {
      opacity: 0.98;
      transform: scale(1.02);
      filter: saturate(1.16);
    }
    100% {
      opacity: 0.34;
      transform: scale(0.82);
      filter: saturate(0.92);
    }
  }

  @keyframes app-icon-button-border-pulse {
    0% {
      border-color: rgb(var(--color-border-strong) / 0.7);
      box-shadow: 0 0 0 0 rgb(var(--color-state-focus) / 0);
    }
    50% {
      border-color: rgb(var(--color-state-focus) / 0.95);
      box-shadow: 0 0 0 2px rgb(var(--color-state-focus) / 0.14);
    }
    100% {
      border-color: rgb(var(--color-border-strong) / 0.7);
      box-shadow: 0 0 0 0 rgb(var(--color-state-focus) / 0);
    }
  }
</style>
